import { animated, useSpring } from '@react-spring/web';
import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import Sticker from 'react-stickyfill';
import { compose } from 'redux';
import styled from 'styled-components';
import { media } from 'ui';
import { OnDesktopAndTablet } from 'ui/media';
import { announcementBannerHeight } from '../../../utils/app-configs';
import TicketMenuItem from '../containers/ticket-menu-item-container';
import { MainMenuLinks } from './main-menu';
import MenuItem from './menu-item';

const SubMenuLinks = styled(MainMenuLinks)`
  z-index: 8;
  ${media.tablet`
    padding-left: 20px;
  `};
`;

const AnimatedDiv = styled(animated.div)`
  position: sticky;
  top: 109px;
`;

const SubMenu = ({
  id,
  extraLinks = [],
  hasInfo = true,
  hasDashboard = true,
  intl,
  dashboardTranslation,
  expand,
  actionButtons = null,
  menuVisible,
  hasTickets = false,
  setMenuVisible,
  base,
  baseType,
}) => {
  const fixedItems = [];
  const location = useLocation();
  const { hasAnnouncementBanner } = useSelector(state => state.user);
  if (hasDashboard) {
    fixedItems.push({
      translationKey: dashboardTranslation || 'nav.subNav.DASHBOARD',
      uri: `/${base ? `${base}/` : ''}dashboard`,
      absolute: true,
    });
  }

  if (hasInfo) {
    fixedItems.push({
      translationKey: 'nav.subNav.INFO',
      uri: `/${base ? `${base}/` : ''}info`,
      absolute: true,
    });
  }

  const allLinks = [...fixedItems, ...extraLinks];

  if (hasTickets) {
    allLinks.push({
      translationKey: 'nav.TICKETS',
      uri: `/${base ? `${base}/` : ''}tickets/object/${id}`,
      isTicket: true,
      absolute: true,
    });
  }

  const translateYpx = -110;
  const updatedTranslateY = hasAnnouncementBanner ?
    `${translateYpx + announcementBannerHeight}px` :
    `${translateYpx}px`;
  const originalTranslateY = hasAnnouncementBanner && window.pageYOffset > 10 ?
    `${announcementBannerHeight}px` :
    '0px';
  const menuProps = useSpring({
    transform: menuVisible ?
      `translateY(${originalTranslateY})` :
      `translateY(${updatedTranslateY})`,
    opacity: menuVisible ? 1 : 0.7,
    zIndex: menuVisible ? 80 : 8,
    config: { duration: 400 },
  });

  return (
    <Sticker>
      <AnimatedDiv
        style={menuProps}
        onMouseEnter={() => window.pageYOffset > 10 && setMenuVisible(true)}
        onMouseLeave={() => window.pageYOffset > 10 && setMenuVisible(false)}
      >
        <SubMenuLinks visible={expand}>
          {allLinks?.map(item =>
            item.isTicket ?
              <TicketMenuItem
                key={item.uri}
                id={id}
                type={
                  item.translationKey ? intl.formatMessage({ id: item.translationKey }) : item.title
                }
                link={item.absolute ?
                  item.uri :
                  `${location.pathname}/${item.uri}`
                }
                exact={item.exact}
                active={item.active}
                baseType={baseType}
              />
              :
              <MenuItem
                key={item.uri}
                type={
                  item.translationKey ? intl.formatMessage({ id: item.translationKey }) : item.title
                }
                link={item.absolute ?
                  item.uri :
                  `${location.pathname}/${item.uri}`
                }
                exact={item.exact}
                active={item.active}
                translationKey={item.translationKey}
              />,
          )}
          <OnDesktopAndTablet>{actionButtons}</OnDesktopAndTablet>
        </SubMenuLinks>
      </AnimatedDiv>
    </Sticker>
  );
};

SubMenu.propTypes = {
  id: PropTypes.string,
  extraLinks: PropTypes.array,
  hasInfo: PropTypes.bool,
  hasDashboard: PropTypes.bool,
  expand: PropTypes.bool,
  dashboardTranslation: PropTypes.string,
  intl: PropTypes.object,
  actionButtons: PropTypes.node,
  menuVisible: PropTypes.bool,
  hasTickets: PropTypes.bool,
  setMenuVisible: PropTypes.func,
  base: PropTypes.string,
  baseType: PropTypes.string,
};

export default compose(injectIntl)(SubMenu);
